import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Group,
  Stack,
  Text,
  Button,
  Table,
  Badge,
  ActionIcon,
  Modal,
  Pagination,
  Select,
  TextInput,
  Divider,
  Paper,
  Grid,
  Title,
  Alert,
  LoadingOverlay,
  Tooltip,
  CopyButton,
  Code,
} from "@mantine/core";
import {
  IconDownload,
  IconEye,
  IconMail,
  IconFileText,
  IconCalendar,
  IconCurrencyEuro,
  IconSearch,
  IconFilter,
  IconRefresh,
  IconCopy,
  IconCheck,
  IconAlertCircle,
  IconInfoCircle,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import { DatePickerInput } from "@mantine/dates";
import { getUserInvoices } from "../misc/api";

const InvoiceScreen = () => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetailModal, setInvoiceDetailModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  // Filters
  const [filters, setFilters] = useState({
    status: "",
    start_date: null,
    end_date: null,
    search: "",
  });

  useEffect(() => {
    loadInvoices();
  }, [pagination.page, filters]);

  const loadInvoices = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });

      // Add filters if they exist
      if (filters.status) params.append("status", filters.status);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.search) params.append("search", filters.search);

      const data = await getUserInvoices();

      if (data.success) {
        setInvoices(data.data);
        setPagination(data.pagination);
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to load invoices",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const viewInvoiceDetails = async (invoiceId) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/invoices/${invoiceId}`, {
        headers: { Authorization: getToken() },
      });
      const data = await response.json();

      if (data.success) {
        setSelectedInvoice(data.data);
        setInvoiceDetailModal(true);
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to load invoice details",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const response = await fetch(`/api/invoices/${invoiceId}/download`, {
        headers: { Authorization: getToken() },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `invoice-${invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        notifications.show({
          title: "Success",
          message: "Invoice downloaded successfully",
          color: "green",
        });
      } else {
        throw new Error("Download failed");
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to download invoice",
        color: "red",
      });
    }
  };

  const resendInvoice = async (invoiceId) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/invoices/${invoiceId}/resend`, {
        method: "POST",
        headers: { Authorization: getToken() },
      });
      const data = await response.json();

      if (data.success) {
        notifications.show({
          title: "Success",
          message: "Invoice sent to your email",
          color: "green",
        });
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to send invoice",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "green";
      case "issued":
        return "blue";
      case "overdue":
        return "red";
      case "cancelled":
        return "gray";
      default:
        return "gray";
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      status: "",
      start_date: null,
      end_date: null,
      search: "",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const getToken = () => {
    return localStorage.getItem("token") || "";
  };

  const InvoiceDetailsModal = () => {
    if (!selectedInvoice) return null;

    const items = JSON.parse(selectedInvoice.items || "[]");

    return (
      <Modal
        opened={invoiceDetailModal}
        onClose={() => setInvoiceDetailModal(false)}
        title={`Invoice ${selectedInvoice.invoice_number}`}
        size="lg"
      >
        <Stack spacing="md">
          {/* Invoice Header */}
          <Grid>
            <Grid.Col span={6}>
              <Stack spacing="xs">
                <Text size="sm" c="dimmed">
                  Invoice Number
                </Text>
                <Group spacing="xs">
                  <Code>{selectedInvoice.invoice_number}</Code>
                  <CopyButton value={selectedInvoice.invoice_number}>
                    {({ copied, copy }) => (
                      <ActionIcon
                        size="sm"
                        color={copied ? "teal" : "gray"}
                        onClick={copy}
                      >
                        {copied ? (
                          <IconCheck size="0.8rem" />
                        ) : (
                          <IconCopy size="0.8rem" />
                        )}
                      </ActionIcon>
                    )}
                  </CopyButton>
                </Group>
              </Stack>
            </Grid.Col>
            <Grid.Col span={6}>
              <Stack spacing="xs">
                <Text size="sm" c="dimmed">
                  Status
                </Text>
                <Badge color={getStatusColor(selectedInvoice.status)}>
                  {selectedInvoice.status.toUpperCase()}
                </Badge>
              </Stack>
            </Grid.Col>
          </Grid>

          <Divider />

          {/* Dates */}
          <Grid>
            <Grid.Col span={4}>
              <Stack spacing="xs">
                <Text size="sm" c="dimmed">
                  Issue Date
                </Text>
                <Text size="sm">{formatDate(selectedInvoice.issue_date)}</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={4}>
              <Stack spacing="xs">
                <Text size="sm" c="dimmed">
                  Due Date
                </Text>
                <Text size="sm">{formatDate(selectedInvoice.due_date)}</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={4}>
              {selectedInvoice.paid_at && (
                <Stack spacing="xs">
                  <Text size="sm" c="dimmed">
                    Paid Date
                  </Text>
                  <Text size="sm">{formatDate(selectedInvoice.paid_at)}</Text>
                </Stack>
              )}
            </Grid.Col>
          </Grid>

          <Divider />

          {/* Items */}
          <div>
            <Text weight={500} mb="sm">
              Invoice Items
            </Text>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Qty</Table.Th>
                  <Table.Th>Unit Price</Table.Th>
                  <Table.Th>Total</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((item, index) => (
                  <Table.Tr key={index}>
                    <Table.Td>{item.description}</Table.Td>
                    <Table.Td>{item.quantity}</Table.Td>
                    <Table.Td>{formatCurrency(item.unit_price)}</Table.Td>
                    <Table.Td>{formatCurrency(item.total)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>

          <Divider />

          {/* Total */}
          <Group justify="flex-end">
            <Text weight={700} size="lg">
              Total: {formatCurrency(selectedInvoice.amount)}
            </Text>
          </Group>

          {/* Notes */}
          {selectedInvoice.notes && (
            <>
              <Divider />
              <div>
                <Text weight={500} mb="xs">
                  Notes
                </Text>
                <Text size="sm" c="dimmed">
                  {selectedInvoice.notes}
                </Text>
              </div>
            </>
          )}

          {/* Actions */}
          <Group justify="flex-end" mt="md">
            <Button
              variant="outline"
              leftSection={<IconMail size="1rem" />}
              onClick={() => resendInvoice(selectedInvoice.invoice_id)}
              disabled={!selectedInvoice.billing_email}
            >
              Resend Email
            </Button>
            <Button
              leftSection={<IconDownload size="1rem" />}
              onClick={() =>
                downloadInvoice(
                  selectedInvoice.invoice_id,
                  selectedInvoice.invoice_number
                )
              }
              disabled={!selectedInvoice.pdf_path}
            >
              Download PDF
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  };

  return (
    <Container size="xl" py="xl">
      <Stack spacing="lg">
        {/* Header */}
        <Group justify="space-between">
          <Title>My Invoices</Title>
          <Button
            leftSection={<IconRefresh size="1rem" />}
            variant="light"
            onClick={loadInvoices}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>

        {/* Filters */}
        <Card withBorder padding="md">
          <Stack spacing="md">
            <Group>
              <TextInput
                placeholder="Search invoices..."
                leftSection={<IconSearch size="1rem" />}
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                style={{ flex: 1 }}
              />
              <Select
                placeholder="Status"
                data={[
                  { value: "", label: "All Statuses" },
                  { value: "issued", label: "Issued" },
                  { value: "paid", label: "Paid" },
                  { value: "overdue", label: "Overdue" },
                  { value: "cancelled", label: "Cancelled" },
                ]}
                value={filters.status}
                onChange={(value) => handleFilterChange("status", value || "")}
                clearable
              />
              <DatePickerInput
                placeholder="Start Date"
                value={filters.start_date}
                onChange={(value) => handleFilterChange("start_date", value)}
                clearable
              />
              <DatePickerInput
                placeholder="End Date"
                value={filters.end_date}
                onChange={(value) => handleFilterChange("end_date", value)}
                clearable
              />
              <Button variant="outline" onClick={clearFilters}>
                Clear
              </Button>
            </Group>
          </Stack>
        </Card>

        {/* Invoices Table */}
        <Card withBorder padding={0}>
          <LoadingOverlay visible={loading} />

          {invoices.length === 0 && !loading ? (
            <Stack align="center" py="xl" spacing="md">
              <IconFileText size="3rem" color="gray" />
              <Text c="dimmed">No invoices found</Text>
              <Text size="sm" c="dimmed">
                Your invoices will appear here once you make purchases
              </Text>
            </Stack>
          ) : (
            <>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Invoice #</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Issue Date</Table.Th>
                    <Table.Th>Due Date</Table.Th>
                    <Table.Th>Amount</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {invoices.map((invoice) => (
                    <Table.Tr key={invoice.invoice_id}>
                      <Table.Td>
                        <Code>{invoice.invoice_number}</Code>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={2}>
                          {invoice.description}
                        </Text>
                      </Table.Td>
                      <Table.Td>{formatDate(invoice.issue_date)}</Table.Td>
                      <Table.Td>{formatDate(invoice.due_date)}</Table.Td>
                      <Table.Td>
                        <Text weight={500}>
                          {formatCurrency(invoice.amount)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={getStatusColor(invoice.status)}>
                          {invoice.status.toUpperCase()}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group spacing="xs">
                          <Tooltip label="View Details">
                            <ActionIcon
                              variant="subtle"
                              onClick={() =>
                                viewInvoiceDetails(invoice.invoice_id)
                              }
                            >
                              <IconEye size="1rem" />
                            </ActionIcon>
                          </Tooltip>
                          {invoice.pdf_path && (
                            <Tooltip label="Download PDF">
                              <ActionIcon
                                variant="subtle"
                                onClick={() =>
                                  downloadInvoice(
                                    invoice.invoice_id,
                                    invoice.invoice_number
                                  )
                                }
                              >
                                <IconDownload size="1rem" />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          {invoice.billing_email && (
                            <Tooltip label="Resend Email">
                              <ActionIcon
                                variant="subtle"
                                onClick={() =>
                                  resendInvoice(invoice.invoice_id)
                                }
                              >
                                <IconMail size="1rem" />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div style={{ padding: "1rem", textAlign: "center" }}>
                  <Pagination
                    value={pagination.page}
                    onChange={(page) =>
                      setPagination((prev) => ({ ...prev, page }))
                    }
                    total={pagination.pages}
                  />
                </div>
              )}
            </>
          )}
        </Card>

        {/* Info Alert */}
        <Alert icon={<IconInfoCircle size="1rem" />} color="blue">
          <Text size="sm">
            Invoices are automatically generated for your transactions. You can
            download PDF copies or request email delivery using the actions
            above.
          </Text>
        </Alert>
      </Stack>

      {/* Invoice Details Modal */}
      <InvoiceDetailsModal />
    </Container>
  );
};

export default InvoiceScreen;
